// @flow

import { createSelector } from 'reselect';
import { getRatio } from './ratio';
import {
  getActivePdbId,
  getBundles as getPdbBundles,
} from '../../../pdb/selectors';
import {
  getReducer,
} from './main';
import type { ActivesState as State } from '../types';
import type { State as ConfigState } from '../../types';
import type { State as PdbState } from '../../../pdb/types';
import type { OrderFormState } from '../../../orderForm/types';
import type { Bundle, Ratio } from '../../../../types/pdb';

type GlobalState = {
  configuration: ConfigState,
  pdb: PdbState,
  orderForm: OrderFormState,
};

/**
 * We export the function to get all bundles for a car here, since we do that for
 * all actives. The reason is that some car properties depend on choices of other
 * car properties.
 */
export const getBundles = createSelector<GlobalState, any, Array<Bundle>, Ratio, Array<Bundle>>(
  [getRatio, getPdbBundles],
  (ratio: Ratio, pdbBundles: Array<Bundle>): Array<Bundle> => pdbBundles.map((bundle: Bundle) => ({
    ...bundle,
    price: bundle.recalculate ? (parseFloat(bundle.price) * ratio).toString() : bundle.price,
  })),
);

type ActiveBundles = $PropertyType<State, 'activeBundles'>;
export const getBundlesFromActives = (state: GlobalState, pdbId?: ?string) => {
  const selectedPdbId = pdbId || getActivePdbId(state);
  const reducer: ActiveBundles = getReducer(state).activeBundles;
  return reducer[selectedPdbId] || null;
};

type BundleId = $PropertyType<Bundle, 'id'>;
export const getActiveBundleId = (state: GlobalState, pdbId?: ?string): ?BundleId => (
  getBundlesFromActives(state, pdbId)
);

export const getActiveBundle = (state: GlobalState, pdbId?: ?string): Bundle[] => {
  const bundles = getBundles(state, pdbId);
  const active = getActiveBundleId(state, pdbId);
  return bundles.filter(x => active === x.id);
};

type AccessoryIds = $PropertyType<$PropertyType<Bundle, 'mapped'>, 'accessoryIds'>;
export const getActiveBundleAccessoryId = (state: GlobalState): AccessoryIds => {
  const activeBundles = getActiveBundle(state);

  const accessoryIdsSet = activeBundles.reduce((collector, bundle) => [
    ...collector,
    ...bundle.mapped.accessoryIds,
  ], []);

  // Use set to make sure only unique values are in the array
  return Array.from(new Set(accessoryIdsSet));
};

type ExtraContractIds = $PropertyType<$PropertyType<Bundle, 'mapped'>, 'extraContractIds'>;
export const getActiveBundleExtraContractIds = (
  state: GlobalState,
  pdbId?: ?string,
): ExtraContractIds => {
  const activeBundles = getActiveBundle(state, pdbId);

  const extraContractIdsSet = activeBundles.reduce((collector, bundle) => [
    ...collector,
    ...bundle.mapped.extraContractIds,
  ], []);

  // Use set to make sure only unique values are in the array
  return Array.from(new Set(extraContractIdsSet));
};
